import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { useBalances } from '../../features/balances/hooks/useBalances';
import { 
    groupBalances, 
    filterOrganizations, 
    calculateSummary
} from '../../features/balances/lib/groupBalances';
import { 
    formatMoney, 
    TYPE_LABELS, 
    TYPE_COLORS,
    type BalanceFilters,
    type Organization,
    type Account,
    type AccountType 
} from '../../features/balances/types/balances.types';

import {
    Card,
    Skeleton,
    EmptyState,
    SectionHeader,
    MoneyDisplay,
    MiniBarChart,
    FilterButton,
    Button,
    cn,
} from '../../shared/ui/kit';

// ═══════════════════════════════════════════════════════════════════════════════
// ИКОНКИ
// ═══════════════════════════════════════════════════════════════════════════════

const Ic = {
    Chevron: ({ open, className }: { open: boolean; className?: string }) => (
        <svg
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            className={cn('transition-transform duration-200', open && 'rotate-180', className)}
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    ),

    Cash: ({ className }: { className?: string }) => (
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={className}>
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
        </svg>
    ),

    NonCash: ({ className }: { className?: string }) => (
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={className}>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
    ),

    History: ({ className }: { className?: string }) => (
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={className}>
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
    ),

    Search: ({ className }: { className?: string }) => (
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={className}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    ),

    Refresh: ({ className, spinning }: { className?: string; spinning?: boolean }) => (
        <svg
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            className={cn(spinning && 'animate-spin', className)}
        >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
    ),

    AlertCircle: ({ className }: { className?: string }) => (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={className}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),

    Info: ({ className }: { className?: string }) => (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={className}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    ),

    Database: ({ className }: { className?: string }) => (
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={className}>
            <ellipse cx="12" cy="5" rx="9" ry="3" />
            <path d="M3 5V19A9 3 0 0 0 21 19V5" />
            <path d="M3 12A9 3 0 0 0 21 12" />
        </svg>
    ),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ
// ═══════════════════════════════════════════════════════════════════════════════

const TypeIcon: React.FC<{ accountName: AccountType; className?: string }> = ({ 
    accountName, 
    className 
}) => {
    const Icon = accountName === 'cash' ? Ic.Cash : Ic.NonCash;
    return <Icon className={className} />;
};

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: СВОДНЫЕ КАРТОЧКИ
// ═══════════════════════════════════════════════════════════════════════════════

interface SummaryCardsProps {
    byCurrency: Record<string, { cash: number; noncash: number }>;
    isMock?: boolean;
}

const SummaryCards: React.FC<SummaryCardsProps> = React.memo(({ byCurrency, isMock }) => {
    const rubCash = byCurrency.RUB?.cash ?? 0;
    const rubNoncash = byCurrency.RUB?.noncash ?? 0;
    const rubTotal = rubCash + rubNoncash;

    const hasOtherCurrencies = Object.keys(byCurrency).filter((c) => c !== 'RUB').length > 0;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-[18px] pb-3.5">
            <Card className={cn(
                "p-4 relative overflow-hidden",
                isMock && "border-amber-200 dark:border-amber-500/30"
            )}>
                {isMock && (
                    <div className="absolute top-2 right-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 
                                       text-amber-700 dark:text-amber-400 font-medium">
                            Demo
                        </span>
                    </div>
                )}
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                    Итого
                </p>
                <MoneyDisplay 
                    amount={rubTotal} 
                    currency="RUB" 
                    size="lg" 
                    className={cn(
                        "text-gray-900 dark:text-gray-100",
                        isMock && "text-amber-700 dark:text-amber-400"
                    )} 
                />
                {hasOtherCurrencies && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">и др. валюты</p>
                )}
            </Card>

            <Card className="p-4">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                    {TYPE_LABELS.cash.full}
                </p>
                <MoneyDisplay 
                    amount={rubCash} 
                    currency="RUB" 
                    size="lg" 
                    className="text-emerald-600 dark:text-emerald-400" 
                />
                <div className="flex items-center gap-1.5 mt-1.5">
                    <div className={cn('w-1.5 h-1.5 rounded-full', TYPE_COLORS.cash.dot)} />
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">RUB</span>
                </div>
            </Card>

            <Card className="p-4">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                    {TYPE_LABELS.noncash.full}
                </p>
                <MoneyDisplay 
                    amount={rubNoncash} 
                    currency="RUB" 
                    size="lg" 
                    className="text-blue-600 dark:text-blue-400" 
                />
                <div className="flex items-center gap-1.5 mt-1.5">
                    <div className={cn('w-1.5 h-1.5 rounded-full', TYPE_COLORS.noncash.dot)} />
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">RUB</span>
                </div>
            </Card>
        </div>
    );
});

SummaryCards.displayName = 'SummaryCards';

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: ПАНЕЛЬ ФИЛЬТРОВ
// ═══════════════════════════════════════════════════════════════════════════════

interface FilterBarProps {
    filters: BalanceFilters;
    onChange: (f: BalanceFilters) => void;
    currencies: string[];
}

const FilterBar: React.FC<FilterBarProps> = React.memo(({ filters, onChange, currencies }) => {
    const setType = useCallback((type: BalanceFilters['type']) => onChange({ ...filters, type }), [filters, onChange]);
    const setCurrency = useCallback((currency: string) => onChange({ ...filters, currency: currency as BalanceFilters['currency'] }), [filters, onChange]);
    const setSearch = useCallback((search: string) => onChange({ ...filters, search }), [filters, onChange]);

    return (
        <div className="px-[18px] pb-4">
            <SectionHeader title="Фильтры">
                <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                    <FilterButton active={filters.type === 'all'} onClick={() => setType('all')}>
                        Все
                    </FilterButton>
                    <FilterButton active={filters.type === 'cash'} onClick={() => setType('cash')}>
                        {TYPE_LABELS.cash.full}
                    </FilterButton>
                    <FilterButton active={filters.type === 'noncash'} onClick={() => setType('noncash')}>
                        {TYPE_LABELS.noncash.full}
                    </FilterButton>

                    <div className="w-px h-4 bg-black/10 dark:bg-white/10 mx-1 hidden sm:block" />

                    <FilterButton active={filters.currency === 'all'} onClick={() => setCurrency('all')}>
                        Все валюты
                    </FilterButton>
                    {currencies.map((c) => (
                        <FilterButton key={c} active={filters.currency === c} onClick={() => setCurrency(c)} variant="accent">
                            {c}
                        </FilterButton>
                    ))}
                </div>
            </SectionHeader>

            <div className="relative flex items-center w-full sm:w-[280px]">
                <div className="absolute left-3 text-gray-400">
                    <Ic.Search />
                </div>
                <input
                    type="text"
                    placeholder="Поиск по организации…"
                    value={filters.search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={cn(
                        'w-full pl-9 pr-3 py-2 rounded-lg',
                        'bg-white dark:bg-white/5',
                        'border border-black/7 dark:border-white/7',
                        'text-[13px] text-gray-700 dark:text-gray-300',
                        'placeholder:text-gray-400',
                        'focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20',
                        'transition-all duration-150'
                    )}
                />
            </div>
        </div>
    );
});

FilterBar.displayName = 'FilterBar';

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: СТРОКА СЧЕТА С ИСТОРИЕЙ
// ═══════════════════════════════════════════════════════════════════════════════

interface AccountRowProps {
    account: Account;
}

const AccountRow: React.FC<AccountRowProps> = React.memo(({ account }) => {
    const [showHistory, setShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState<number[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const loadingRef = useRef(false);
    const historyLoadedRef = useRef(false);

    const colors = TYPE_COLORS[account.accountName];
    const typeLabel = TYPE_LABELS[account.accountName];

    useEffect(() => {
        if (!showHistory || historyLoadedRef.current || loadingRef.current) return;

        loadingRef.current = true;

        const rafId = requestAnimationFrame(() => {
            setIsLoadingHistory(true);
        });

        const timer = setTimeout(() => {
            const mock = Array.from({ length: 20 }, () => account.amount * (0.8 + Math.random() * 0.4));
            setHistoryData(mock);
            setIsLoadingHistory(false);
            loadingRef.current = false;
            historyLoadedRef.current = true;
        }, 300);

        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(timer);
        };
    }, [showHistory, account.amount]);

    const toggleHistory = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowHistory((prev) => !prev);
    }, []);

    const toggleRow = useCallback(() => {
        setShowHistory((prev) => !prev);
    }, []);

    return (
        <div className="border-b border-black/6 dark:border-white/6 last:border-b-0">
            <div
                onClick={toggleRow}
                className={cn(
                    'flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 py-3 cursor-pointer',
                    'hover:bg-gray-50 dark:hover:bg-white/[0.03]',
                    'transition-colors duration-150'
                )}
                style={{ gridTemplateColumns: '1fr auto auto' }}
            >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className={cn('w-2 h-2 rounded-full flex-shrink-0', colors.dot)} />
                    <TypeIcon accountName={account.accountName} className={cn('w-4 h-4 flex-shrink-0', colors.text)} />
                    <span className={cn('text-[13px] font-medium truncate', colors.text)}>
                        {typeLabel.full}
                    </span>
                    <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded flex-shrink-0">
                        {account.currency}
                    </span>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
                    <MoneyDisplay
                        amount={account.amount}
                        currency={account.currency}
                        className={cn(
                            'text-gray-900 dark:text-gray-100',
                            account.amount < 0 && 'text-red-600 dark:text-red-400'
                        )}
                    />
                    <button
                        onClick={toggleHistory}
                        className={cn(
                            'text-[11px] px-2 py-1 rounded-md border transition-all duration-150',
                            'flex items-center gap-1 flex-shrink-0',
                            showHistory
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400'
                                : 'bg-white dark:bg-white/5 border-black/7 dark:border-white/7 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/10'
                        )}
                    >
                        История <Ic.History />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {showHistory && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className={cn(
                            'px-4 py-3 bg-gray-50 dark:bg-white/[0.03]',
                            'border-t border-black/6 dark:border-white/6'
                        )}
                    >
                        <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                            История изменений
                        </p>

                        {isLoadingHistory ? (
                            <div className="h-14 flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                            </div>
                        ) : (
                            <>
                                <MiniBarChart
                                    data={historyData}
                                    color={colors.chart}
                                    height={56}
                                    className="mb-4"
                                />

                                <div className="overflow-x-auto -mx-4 px-4">
                                    <table className="w-full text-[11px] min-w-[400px]">
                                        <thead>
                                            <tr className="text-gray-400 dark:text-gray-500 border-b border-black/6 dark:border-white/6">
                                                <th className="text-left font-medium py-1.5 whitespace-nowrap">Дата и время</th>
                                                <th className="text-left font-medium py-1.5 whitespace-nowrap">Тип</th>
                                                <th className="text-left font-medium py-1.5 whitespace-nowrap">Валюта</th>
                                                <th className="text-right font-medium py-1.5 whitespace-nowrap">Сумма</th>
                                                <th className="text-right font-medium py-1.5 whitespace-nowrap">Изменение</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-700 dark:text-gray-300 font-mono">
                                            <tr className="border-b border-black/4 dark:border-white/4 last:border-b-0">
                                                <td className="py-2 whitespace-nowrap">25.03.2026 14:32</td>
                                                <td className="py-2 whitespace-nowrap">{typeLabel.short}</td>
                                                <td className="py-2 whitespace-nowrap">{account.currency}</td>
                                                <td className="text-right py-2 whitespace-nowrap">{formatMoney(account.amount, account.currency)}</td>
                                                <td className="text-right py-2 whitespace-nowrap text-emerald-600 dark:text-emerald-400">▲ +500 000</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 whitespace-nowrap">25.03.2026 14:27</td>
                                                <td className="py-2 whitespace-nowrap">{typeLabel.short}</td>
                                                <td className="py-2 whitespace-nowrap">{account.currency}</td>
                                                <td className="text-right py-2 whitespace-nowrap">{formatMoney(account.amount * 0.98, account.currency)}</td>
                                                <td className="text-right py-2 whitespace-nowrap text-red-500 dark:text-red-400">▼ −200 000</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
});

AccountRow.displayName = 'AccountRow';

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: КАРТОЧКА ОРГАНИЗАЦИИ
// ═══════════════════════════════════════════════════════════════════════════════

interface OrgCardProps {
    org: Organization;
    index: number;
    defaultOpen?: boolean;
}

const OrgCard: React.FC<OrgCardProps> = React.memo(({ org, index, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const accountCount = org.accounts.length;
    const mainCurrency = Object.keys(org.totalByCurrency)[0] || 'RUB';
    const mainTotal = org.totalByCurrency[mainCurrency] ?? 0;

    const accountsText = useMemo(() => {
        if (accountCount === 1) return '1 счёт';
        if (accountCount < 5) return `${accountCount} счёта`;
        return `${accountCount} счетов`;
    }, [accountCount]);

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    return (
        <Card
            className={cn(
                'overflow-hidden transition-all duration-200',
                isOpen && 'ring-1 ring-black/8 dark:ring-white/8'
            )}
            style={{ animationDelay: `${index * 50}ms` }}
        >
            <div
                onClick={toggleOpen}
                className={cn(
                    'flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 py-3.5 cursor-pointer',
                    'hover:bg-gray-50 dark:hover:bg-white/[0.03]',
                    'transition-colors duration-150'
                )}
            >
                 <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                        className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center',
                            'text-white text-[13px] font-semibold flex-shrink-0',
                            'bg-gradient-to-br',
                            org.avatarGradient
                        )}
                    >
                        {org.avatar}
                    </div>
                    <span className="text-[14px] font-medium text-gray-900 dark:text-gray-100 truncate">
                        {org.name}
                    </span>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 w-full sm:w-auto pl-11 sm:pl-0">
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {accountsText}
                    </span>
                    <MoneyDisplay
                        amount={mainTotal}
                        currency={mainCurrency}
                        className="text-gray-900 dark:text-gray-100 flex-shrink-0"
                    />
                    <Ic.Chevron open={isOpen} className="flex-shrink-0 text-gray-400" />
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-black/6 dark:border-white/6"
                    >
                        {org.accounts.map((acc) => (
                            <AccountRow key={acc.id} account={acc} />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Card>
    );
});

OrgCard.displayName = 'OrgCard';

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: СКЕЛЕТОН ЗАГРУЗКИ
// ═══════════════════════════════════════════════════════════════════════════════

const BalancesSkeleton: React.FC = () => (
    <div className="pb-8 animate-in fade-in duration-200">
        <div className="px-[18px] pt-4 pb-2.5">
            <Skeleton className="w-48 h-5 mb-2" />
            <Skeleton className="w-64 h-3" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-[18px] pb-3.5">
            {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[80px]" />
            ))}
        </div>

        <div className="px-[18px] pb-4">
            <Skeleton className="w-full h-10 mb-2" />
        </div>

        <div className="px-[18px] space-y-2">
            {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[60px]" />
            ))}
        </div>
    </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: СОСТОЯНИЕ ОШИБКИ
// ═══════════════════════════════════════════════════════════════════════════════

interface ErrorStateProps {
    error: Error;
    onRetry: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => (
    <div className="flex flex-col items-center justify-center min-h-[50vh] px-[18px]">
        <Ic.AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 text-center mb-2">
            Не удалось загрузить данные
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            {error.message || 'Произошла ошибка при загрузке остатков'}
        </p>
        <Button onClick={onRetry} variant="primary" icon={<Ic.Refresh />}>
            Попробовать снова
        </Button>
    </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: ПУСТОЕ СОСТОЯНИЕ ФИЛЬТРОВ
// ═══════════════════════════════════════════════════════════════════════════════

const EmptyFilterState: React.FC = () => (
    <EmptyState
        icon={<Ic.Search className="w-8 h-8" />}
        title="Ничего не найдено"
        desc="Попробуйте изменить фильтры или поиск"
    />
);

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: БЕЙДЖ DEMO-РЕЖИМА
// ═══════════════════════════════════════════════════════════════════════════════

const DemoBadge: React.FC = () => (
    <motion.span
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full 
                   bg-amber-100 dark:bg-amber-900/30 
                   text-amber-700 dark:text-amber-400 
                   text-[10px] font-medium border border-amber-200 dark:border-amber-500/30"
    >
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
        Demo
    </motion.span>
);

// ═══════════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: ПРЕДУПРЕЖДЕНИЕ О DEMO-ДАННЫХ
// ═══════════════════════════════════════════════════════════════════════════════

const DemoWarning: React.FC = () => (
    <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mx-[18px] mb-4 p-3 rounded-lg 
                   bg-amber-50 dark:bg-amber-900/10 
                   border border-amber-200 dark:border-amber-500/20"
    >
        <div className="flex items-start gap-2">
            <Ic.Info className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-amber-800 dark:text-amber-200">
                    Демонстрационный режим
                </p>
                <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-0.5">
                    Отображаются тестовые данные.
                </p>
            </div>
        </div>
    </motion.div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// ГЛАВНЫЙ КОМПОНЕНТ СТРАНИЦЫ
// ═══════════════════════════════════════════════════════════════════════════════

export function BalancesPage(): React.ReactElement {
    const { 
        data: rawData, 
        isLoading, 
        hasData,           // ← из useBalances (true если есть данные)
        isMockData,        // ← из useBalances (флаг демо-режима)
        error, 
        refresh, 
        lastUpdateText,
        isFetching
    } = useBalances();

    const [filters, setFilters] = useState<BalanceFilters>({
        type: 'all',
        currency: 'all',
        search: '',
    });

    const [isRefreshing, setIsRefreshing] = useState(false);

    // Определяем режим работы (мок или реальный API)
    const isMockMode = isMockData;

    // Мемоизированные вычисления
    const { organizations, currencies, summary, nextUpdateMinutes } = useMemo(() => {
        if (!rawData.length) {
            return {
                organizations: [],
                currencies: [],
                summary: { byCurrency: {}, totalCash: 0, totalNoncash: 0, grandTotalRub: 0 },
                nextUpdateMinutes: 5,
            };
        }

        const grouped = groupBalances(rawData);
        const filtered = filterOrganizations(grouped, filters);
        const allCurrencies = Array.from(new Set(rawData.map((i) => i.currency))).sort();

        const now = new Date();
        const minutes = now.getMinutes();
        const nextUpdate = 5 - (minutes % 5);

        return {
            organizations: filtered,
            currencies: allCurrencies,
            summary: calculateSummary(grouped),
            nextUpdateMinutes: nextUpdate,
        };
    }, [rawData, filters]);

    // Обработчик обновления
    const handleRefresh = useCallback(async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
            await refresh();
        } finally {
            setIsRefreshing(false);
        }
    }, [refresh, isRefreshing]);

    // Состояние загрузки (показываем skeleton только при первой загрузке)
    if (isLoading && !hasData) {
        return <BalancesSkeleton />;
    }

    // Состояние ошибки
    if (error && !hasData) {
        return <ErrorState error={error as Error} onRetry={handleRefresh} />;
    }

    return (
        <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }}
            className="pb-8 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
            {/* HEADER */}
            <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 px-[18px] pt-4 pb-2.5">
                <div className="flex items-center gap-3">
                    <div>
                        <h1 className="text-[17px] font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                            Остатки денежных средств
                        </h1>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-2">
                            <span>Данные из 1С · обновление каждые 5 мин</span>
                            {isMockMode && <DemoBadge />}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {lastUpdateText && (
                        <div className="flex items-center gap-2 text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                            <span className={cn(
                                "w-1.5 h-1.5 rounded-full",
                                isFetching ? "bg-amber-500 animate-pulse" : "bg-emerald-500"
                            )} />
                            <span className="hidden sm:inline">Синхронизировано:</span>
                            <span>{lastUpdateText}</span>
                        </div>
                    )}
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing || isFetching}
                        className={cn(
                            'flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg',
                            'bg-white dark:bg-white/5',
                            'border border-black/7 dark:border-white/7',
                            'text-gray-500 dark:text-gray-400',
                            'hover:bg-gray-50 dark:hover:bg-white/10',
                            'active:scale-[0.98]',
                            'transition-all duration-150',
                            'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                    >
                        <Ic.Refresh spinning={isRefreshing || isFetching} />
                        <span className="hidden sm:inline">
                            {isRefreshing ? 'Обновление...' : 'Обновить'}
                        </span>
                    </button>
                </div>
            </header>

            {/* ПРЕДУПРЕЖДЕНИЕ О DEMO-РЕЖИМЕ */}
            <AnimatePresence>
                {isMockMode && <DemoWarning />}
            </AnimatePresence>

            {/* СВОДНЫЕ КАРТОЧКИ */}
            <SummaryCards byCurrency={summary.byCurrency} isMock={isMockMode} />

            {/* ПАНЕЛЬ ФИЛЬТРОВ */}
            <FilterBar filters={filters} onChange={setFilters} currencies={currencies} />

            {/* СПИСОК ОРГАНИЗАЦИЙ */}
            <section className="px-[18px] space-y-2">
                {organizations.length === 0 ? (
                    <EmptyFilterState />
                ) : (
                    organizations.map((org, i) => (
                        <OrgCard key={org.id} org={org} index={i} defaultOpen={i === 0} />
                    ))
                )}
            </section>

            {/* FOOTER */}
            <footer className="px-[18px] mt-6 pt-4 border-t border-black/6 dark:border-white/6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                    <span>
                        {isMockMode ? (
                            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <Ic.Database className="w-3 h-3" />
                                Демо-данные · следующее обновление через {nextUpdateMinutes} мин
                            </span>
                        ) : (
                            <>Данные из 1С · следующее обновление через {nextUpdateMinutes} мин</>
                        )}
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">
                        {isMockMode ? 'Режим разработки' : 'Синхронизация каждые 5 мин'}
                    </span>
                </div>
            </footer>
        </motion.div>
    );
}